import {Card} from 'platform/components';
import {Align, Box, Display, Heading, Hide, HStack, Link, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../../../components/Alert/Alert';
import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import i18n from '../../../../i18n/i18n';
import {PriceReportType} from '../../../../types/PriceReportType';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {PriceRecommendationChart} from './components/PriceRecommendationChart';
import {WaterfallChart} from './components/WaterfallChart';

interface MaxPurchasePriceCardProps extends TestIdProps {
  priceReport?: PriceReportType;
}

export function MaxPurchasePriceCard(props: MaxPurchasePriceCardProps) {
  const {isPrint} = useIsPrintForced();
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();

  const middleLevel = props.priceReport?.markets?.[0].purchasePrice?.[2];

  if (isNil(middleLevel)) {
    return null;
  }

  const maxPurchasePrice = middleLevel?.final_purchase_price ?? 0;
  const currency = props.priceReport?.sellingPrice.recommended.currency ?? '';
  const chartData = [
    {key: 'totalCosts', value: middleLevel?.total_costs ?? 0},
    {key: 'financingCost', value: -(middleLevel?.financing_cost ?? 0)},
    {key: 'depreciationCost', value: -(middleLevel?.depreciation_cost ?? 0)},
    {key: 'stockKeepingCost', value: -(middleLevel?.stock_keeping_cost ?? 0)},
    {key: 'adminFeesCost', value: -(middleLevel?.admin_fees_cost ?? 0)},
    {key: 'prepCost', value: -(middleLevel?.prep_cost ?? 0)},
    {key: 'riskCost', value: -(middleLevel?.risk_cost ?? 0)},
    {key: 'lostMargin', value: -(middleLevel?.lost_margin ?? 0)},
    {key: 'minMargin', value: -(middleLevel?.min_margin ?? 0)},
  ].map((item) => ({
    ...item,
    name: i18n.t(`entity.grossPurchasePrice.${item.key}`),
    tooltip: i18n.t(`entity.grossPurchasePrice.${item.key}Tooltip`),
  }));
  const priceRecommendations =
    props.priceReport?.markets?.[0].purchasePrice?.map((item) => item.final_purchase_price) ?? [];
  const middleLevelFormatted = Object.fromEntries(
    Object.entries(middleLevel).map(([key, value]) => [key, formatNumber(value)])
  );
  const pdfFile = match(i18n.language)
    .with('cs', () => 'Jak se pocitaji naklady.pdf')
    .with('de', () => 'Wie werden die Kosten berechnet.pdf')
    .with('es', () => 'Como se calculan los costes.pdf')
    .with('fr', () => 'Comment les couts sont-ils calcules.pdf')
    .with('it', () => 'Come vengono calcolati i costi.pdf')
    .with('nl', () => 'Hoe worden de kosten berekend.pdf')
    .with('pl', () => 'Jak obliczane sa koszty.pdf')
    .with('sk', () => 'Ako sa vypocitavaju naklady.pdf')
    .otherwise(() => 'How are the costs calculated.pdf');

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('grossPurchasePriceTitle', props)}>
                {i18n.t('entity.grossPurchasePrice.title')}
              </Heading>
            </Align>

            <HStack spacing={2} align="baseline" justify="center">
              <Heading size={1} color="link" alternative>
                {i18n.t('entity.grossPurchasePrice.max')}
              </Heading>
              <Display
                size={1}
                color="link"
                data-testid={suffixTestId('maxGrossPurchasePrice', props)}
              >
                {formatCurrency(maxPurchasePrice, currency)}
              </Display>
            </HStack>

            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('maxPurchasePriceDescription', props)}
            >
              {i18n.t('entity.grossPurchasePrice.description', middleLevelFormatted)}
              <Hide when={isPrint}>
                {' '}
                <Link size="small" href={`assets/pdf/${pdfFile}`} target="_blank">
                  {i18n.t('entity.grossPurchasePrice.link')}
                </Link>
              </Hide>
            </Text>

            <WaterfallChart
              data={chartData}
              currency={currency}
              data-testid={suffixTestId('maxPurchasePriceChart', props)}
            />

            <Alert
              icon="action/info_outline"
              data-testid={suffixTestId('maxPurchasePriceAlert', props)}
            >
              {i18n.t('entity.grossPurchasePrice.alert')}
            </Alert>

            <PriceRecommendationChart
              recommendations={priceRecommendations}
              currency={currency}
              data-testid={suffixTestId('priceRecommendationChart', props)}
            />
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
